import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErrorDetail } from "./api";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "../components/LoadState";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, obj = authed
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("lf_token", data.token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = e.userMessage || formatErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn(e.userMessage || "Logout request failed.");
    }
    localStorage.removeItem("lf_token");
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="loading-screen">
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "CEO" ? "/ceo" : "/manager"} replace />;
  }
  return children;
}
